import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { ArrowLeftIcon, Bell, UserIcon } from "@/components/icons";
import { Button, Textarea, toast } from "@/components/kit";
import type { CreateRestockWaitlistDTO } from "@/modules/restock-waitlist/domain/contracts";
import {
  CustomerIdentityFields,
  customerIdentityToInput,
  type CustomerIdentityValue,
} from "@/modules/orders/presentation/ui/CustomerIdentityFields";
import { ProductFormSectionLike } from "@/modules/orders/presentation/ui/OrderSection";
import {
  ProductPickerModal,
  type PickedVariant,
} from "@/modules/orders/presentation/ui/ProductPickerModal";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useCustomerQuery } from "@/modules/customers/presentation/api/queries";
import {
  useCreateRestockWaitlist,
  useRestockWaitlistImportHintsQuery,
} from "../api/queries";
import {
  WaitlistLinesEditor,
  waitlistFormLineFromPick,
  type WaitlistFormLine,
} from "../ui/WaitlistLinesEditor";

const INITIAL_CUSTOMER: CustomerIdentityValue = {
  name: "",
  phone: "",
  customerId: undefined,
  forceNewCustomer: false,
};

export function RestockWaitlistFormPage() {
  const nav = useNavigate();
  const search = useSearch({ strict: false }) as { customerId?: string };
  const createEntry = useCreateRestockWaitlist();
  const prefilledCustomer = useCustomerQuery(search.customerId ?? "");

  const [customer, setCustomer] = useState<CustomerIdentityValue>(
    INITIAL_CUSTOMER,
  );
  const [customerHydrated, setCustomerHydrated] = useState(false);
  const [staffNote, setStaffNote] = useState("");
  const [lines, setLines] = useState<WaitlistFormLine[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);

  const variantIds = useMemo(
    () => lines.map((line) => line.variant.variantId),
    [lines],
  );
  const importHintsQuery = useRestockWaitlistImportHintsQuery(variantIds);

  const pickedVariantIds = useMemo(
    () => new Set(variantIds),
    [variantIds],
  );

  useEffect(() => {
    if (!search.customerId || customerHydrated || !prefilledCustomer.data) {
      return;
    }
    setCustomer({
      name: prefilledCustomer.data.name,
      phone: prefilledCustomer.data.phone ?? "",
      customerId: prefilledCustomer.data.id,
      forceNewCustomer: false,
    });
    setCustomerHydrated(true);
  }, [search.customerId, customerHydrated, prefilledCustomer.data]);

  const handlePick = (variant: PickedVariant) => {
    if (pickedVariantIds.has(variant.variantId)) {
      toast.error("ສິນຄ້ານີ້ມີໃນລາຍການແລ້ວ");
      return;
    }
    setLines((prev) => [...prev, waitlistFormLineFromPick(variant)]);
    setPickerOpen(false);
  };

  const buildPayload = (): CreateRestockWaitlistDTO => ({
    customer: customerIdentityToInput(customer),
    lines: lines.map((line) => ({
      variantId: line.variant.variantId,
      quantityRequested: line.quantity,
    })),
    staffNote: staffNote.trim() || null,
  });

  const handleSubmit = async () => {
    if (!customer.name.trim()) {
      toast.error("ກະລຸນາໃສ່ຊື່ລູກຄ້າ");
      return;
    }
    if (lines.length === 0) {
      toast.error("ກະລຸນາເພີ່ມຢ່າງໜ້ອຍ 1 ລາຍການ");
      return;
    }
    for (const line of lines) {
      if (!Number.isInteger(line.quantity) || line.quantity < 1) {
        toast.error("ຈຳນວນຕ້ອງເປັນຈຳນວນເຕັມ ≥ 1");
        return;
      }
    }

    try {
      const entry = await createEntry.mutateAsync(buildPayload());
      toast.success("ບັນທຶກລາຍການສຳເລັດ");
      nav({
        to: "/app/restock-waitlist/$id",
        params: { id: entry.id },
      });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "ບັນທຶກບໍ່ສຳເລັດ");
    }
  };

  return (
    <>
      <Header />
      <Main className="mx-auto flex w-full max-w-2xl flex-col gap-6 pb-24">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex flex-col gap-1">
            <h2 className="font-display text-2xl text-foreground sm:text-3xl">
              ບັນທຶກລາຍການລໍຖ້າ
            </h2>
            <p className="text-muted-foreground text-sm">
              ລູກຄ້າສົນໃຈສິນຄ້າທີ່ຫມົດສະຕັອກຫຼືກຳລັງສັ່ງມາ — ເພີ່ມໄດ້ຫຼາຍລາຍການໃນບັນທຶກເດຍວ
            </p>
            <div className="mt-2 h-0.5 w-16 rounded-full bg-secondary/70" />
          </div>
          <Button
            variant="outline"
            className="rounded-full border-secondary/40"
            onClick={() => nav({ to: "/app/restock-waitlist" })}
          >
            <ArrowLeftIcon data-icon="inline-start" />
            ກັບ
          </Button>
        </div>

        <ProductFormSectionLike title="ລູກຄ້າ" icon={UserIcon}>
          <CustomerIdentityFields
            value={customer}
            onChange={setCustomer}
            idPrefix="waitlist-cust"
          />
        </ProductFormSectionLike>

        <ProductFormSectionLike title="ສິນຄ້າ" icon={Bell}>
          <WaitlistLinesEditor
            lines={lines}
            importHintsByVariant={importHintsQuery.hintsByVariant}
            importHintsLoading={importHintsQuery.isLoading}
            onAddClick={() => setPickerOpen(true)}
            onRemoveLine={(lineKey) =>
              setLines((prev) => prev.filter((line) => line.lineKey !== lineKey))
            }
            onQuantityChange={(lineKey, quantity) =>
              setLines((prev) =>
                prev.map((line) =>
                  line.lineKey === lineKey ? { ...line, quantity } : line,
                ),
              )
            }
          />
        </ProductFormSectionLike>

        <ProductFormSectionLike title="ຫມາຍເຫດພະນັກງານ">
          <Textarea
            className="min-h-24 rounded-xl"
            value={staffNote}
            onChange={(e) => setStaffNote(e.target.value)}
            placeholder="ບັນທຶກພາຍໃນ (ບໍ່ສົ່ງໃຫ້ລູກຄ້າ)"
          />
        </ProductFormSectionLike>

        <div className="sticky bottom-4 z-10">
          <Button
            className="w-full rounded-full"
            disabled={createEntry.isPending}
            onClick={handleSubmit}
          >
            {createEntry.isPending ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກລາຍການ"}
          </Button>
        </div>

        <ProductPickerModal
          open={pickerOpen}
          onOpenChange={setPickerOpen}
          onPick={handlePick}
          allowZeroStock
          excludeBundles
          subtitle="ເລືອກສິນຄ້າທີ່ລູກຄ້າລໍຖ້າ"
        />
      </Main>
    </>
  );
}
